import CustomStore from "devextreme/data/custom_store";
import jwtService from "@service/jwt-service";
import { CommonConst } from "@constants/common-const";
import { CommonUtil } from "@utils/CommonUtil";
import { _getHCostModel } from "@service/hcost/hcost-model-service";
import { HCostConst } from "@constants/hcost/hcost-const";
import { HCOST_RESULT_FACTOR_DATA } from "@constants/hcost/hcost-data";
import { cloneDeep } from "lodash-es";
import { getHCostFactorList } from "@service/hcost/hcost-factor-service";
import { checkVersion, QA_FLAG_STAGING } from "@vhows/util";
import DataSource from "devextreme/data/data_source";
import Axios, { AxiosResponse } from "axios";
import { commonNotify } from "@utils/common";

const axios = jwtService.axiosIns;

type hcostResultDataSource = {
  data: HcostResult[] | HcostResultV2[];
  totalCount: number;
};

type HCostInboundDataSource = {
  data: HCostInbound[];
  totalCount: number;
};

function makeApiPath() {
  return checkVersion("0.0.6", QA_FLAG_STAGING) ? "/v3" : "";
}

// 간편견적 결과 목록 조회
async function getHCostResults(params: string, config?: any) {
  return await axios.get<hcostResultDataSource>(`/hcosts/results?${params}`, config);
}

// 간편견적 결과 조희
async function _getHCostResult(resultId: number) {
  return await axios.get(`/hcosts/results/${resultId}`);
}

// 간편견적 결과 등록
async function createHCostResult(...args: any[]) {
  return await axios.post(`/hcosts/results`, ...args);
}

// 간편견적 결과 수정
async function updateHCostResult(...args: any[]) {
  return await axios.put("/hcosts/results", ...args);
}

// 간편견적 결과 목록 조회 - 인바운드
async function getHCostResultInbounds(params: string, config?: any) {
  return await axios.get<HCostInboundDataSource>(`/inbounds/hcosts?${params}`, config);
}

// 간편견적 결과 조회 - 인바운드
async function _getHCostResultInbound(id: number) {
  return await axios.get<HCostInbound>(`/inbounds/hcosts/${id}`);
}

// 간편견적 결과 등록 - 인바운드
async function createHCostResultInbound(...args: any[]) {
  return await axios.post(`/inbounds/hcosts`, ...args);
}

// 간편견적 결과 수정 - 인바운드
async function editHCostResultInbound(resultId: number, ...args: any) {
  return await axios.put(`/inbounds/hcosts/${resultId}`, ...args);
}

// 간편견적 결과 삭제 - 인바운드
async function removeHCostResultInbound(resultId: number) {
  return await axios.delete(`/inbounds/hcosts/${resultId}`);
}

// S3 Presigned Url 생성 요청
async function getCounselFileUrlInbound(id: number, params: any) {
  return await axios.get(`/inbounds/hcosts/${id}/presignedurl?${params}`);
}

// S3 읽기용 Presigned Url 생성 요청
async function getCounselPresignedurlFileUrlInbound(id: number, attachedFildId: number) {
  return await axios.get(`/inbounds/hcosts/${id}/presignedurl/${attachedFildId}`);
}

// 간편견적(운영자) 목록 조회 - 인바운드
async function getAdminHCostResultInbounds(params: string, config?: any) {
  return await axios.get<HCostInboundDataSource>(`/admin/inbounds/hcosts?${params}`, config);
}

// 간편견적(운영자) 결과 조회 - 인바운드
async function _getAdminHCostResultInbound(id: number) {
  return await axios.get<HCostInbound>(`/admin/inbounds/hcosts/${id}`);
}

// 간편견적(운영자) 결과 등록 - 인바운드
async function createAdminHCostResultInbound(...args: any[]) {
  return await axios.post(`/admin/inbounds/hcosts`, ...args);
}

// 간편견적(운영자) 결과 수정 - 인바운드
async function editAdminHCostResultInbound(resultId: number, ...args: any) {
  return await axios.put(`/admin/inbounds/hcosts/${resultId}`, ...args);
}

// 간편견적(운영자) 결과 삭제 - 인바운드
async function removeAdminHCostResultInbound(resultId: number) {
  return await axios.delete(`/admin/inbounds/hcosts/${resultId}`);
}

// S3 Presigned Url 생성 요청 (운영자)
async function getCounselFileUrl(id: number, params: any) {
  return await axios.get(`/admin/inbounds/hcosts/${id}/presignedurl?${params}`);
}

// S3 읽기용 Presigned Url 생성 요청
async function getCounselPresignedurlFileUrl(id: number, attachedFildId: number) {
  return await axios.get(`/admin/inbounds/hcosts/${id}/presignedurl/${attachedFildId}`);
}
// 간편견적(운영자) 복제 - 인바운드
async function cloneAdminHCostResultInbound(resultId: number, companyId: number, ...args: any[]) {
  return await axios.post(`/admin/inbounds/hcosts/${resultId}?companyId=${companyId}`, ...args);
}
// 시공상담 복사(중개 수락) (운영자) - 인바운드
async function cloneAdminBrokerageInbound(resultId: number, companyId: number, ...args: any[]) {
  return await axios.post(`/admin/inbounds/hcosts/${resultId}`, { companyId: companyId }, ...args);
}
// 시공상담 복사(중개 수락) - 인바운드
async function cloneBrokerageInbound(resultId: number, ...args: any[]) {
  return await axios.post(`/inbounds/hcosts/${resultId}`, ...args);
}

/**
 * 간편견적 결과 저장하기
 */
export async function saveHCostResult(
  modifyType: string,
  hcostResult: HcostResult | HcostResultV2,
  companyId?: number,
  hcostFactors?: HcostFactor[],
) {
  let data;
  if (hcostFactors) {
    data = createSelectedResultFactors(hcostResult, hcostFactors);
  } else {
    data = hcostResult;
  }

  data = {
    ...data,
    companyId,
  };
  // 통신
  try {
    if (modifyType === CommonConst.MODIFY_TYPE_CREATE) {
      await createHCostResult(data, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    } else if (modifyType === CommonConst.MODIFY_TYPE_UPDATE) {
      await createHCostResult(data, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    } else if (modifyType === CommonConst.MODIFY_TYPE_INBOUND_CREATE) {
      await createHCostResultInbound(data, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    } else if (modifyType === CommonConst.MODIFY_TYPE_INBOUND_UPDATE) {
      const { data: inboundResult } = await editHCostResultInbound(hcostResult.id, data, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      commonNotify(CommonConst.completeSave);
      return inboundResult;
    }
    commonNotify(CommonConst.completeSave);
  } catch (e: any) {
    if (e.name === "NavigationDuplicated") {
      // location.reload();
    } else {
      commonNotify(CommonConst.errorSave, "error");
    }
    throw e;
  } finally {
  }
}

export async function saveHCostInbound(modifyType: string, hcostInbound: HCostInbound, accessType?: string) {
  // 통신
  try {
    if (modifyType === CommonConst.MODIFY_TYPE_INBOUND_CREATE && !accessType) {
      return await createHCostResultInbound(hcostInbound, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    } else if (modifyType === CommonConst.MODIFY_TYPE_INBOUND_UPDATE && !accessType) {
      const { data: inboundResult } = await editHCostResultInbound(hcostInbound.id, hcostInbound, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      commonNotify(CommonConst.completeSave);
      return inboundResult;
    } else if (modifyType === CommonConst.MODIFY_TYPE_INBOUND_CREATE && accessType === CommonConst.ACCESS_TYPE_ADMIN) {
      return await createAdminHCostResultInbound(hcostInbound, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    } else if (modifyType === CommonConst.MODIFY_TYPE_INBOUND_UPDATE && accessType === CommonConst.ACCESS_TYPE_ADMIN) {
      const { data: inboundResult } = await editAdminHCostResultInbound(hcostInbound.id, hcostInbound, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      commonNotify(CommonConst.completeSave);
      return inboundResult;
    }
    commonNotify(CommonConst.completeSave);
  } catch (e: any) {
    if (e.name === "NavigationDuplicated") {
      // location.reload();
    } else if (e.response.data.code === "COMMON-E400") {
      commonNotify(e.response.data.message, "error");
    } else {
      commonNotify(CommonConst.errorSave, "error");
    }
    throw e;
  } finally {
  }
}

/**
 * 견적 상담 복제하기
 * */
export async function cloneCounsel(accessType: string, id: number, companyId?: number) {
  // 통신
  try {
    if (accessType === CommonConst.ACCESS_TYPE_ADMIN) {
      return await cloneAdminHCostResultInbound(id, companyId, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    }
  } catch (e: any) {
    commonNotify(CommonConst.errorSave, "error");
    throw e;
  } finally {
  }
}

/** 시공상담 복사(중개 수락) */
export async function cloneBrokerage(accessType: string, id: number, companyId?: number) {
  try {
    if (accessType === CommonConst.ACCESS_TYPE_ADMIN) {
      return await cloneAdminBrokerageInbound(id, companyId, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    } else if (accessType === CommonConst.ACCESS_TYPE_MY) {
      return await cloneBrokerageInbound(id, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    }
  } catch (e: any) {
    commonNotify("중개 수락 중 오류가 발생했습니다!", "error");
    throw e;
  } finally {
  }
}

/**
 * 간편견적 등록하기
 */
export async function getHCostResultCreate(
  modelKey: string,
  hcostResult: HcostResult | HcostResultV2 = null,
  setHcostResult: any = null,
  setHcostFactors: any = null,
) {
  try {
    // 조회
    const { data } = await _getHCostModel(modelKey);

    // 간편견적 요소 불러오기
    if (hcostResult) {
      const createResultData = createModelToResult(data, hcostResult);
      await getHCostFactorList(createResultData, setHcostResult, setHcostFactors);
    }
  } catch (e) {
    console.log(e);

    commonNotify(CommonConst.errorGet, "error");
    throw e;
  } finally {
  }
}

/**
 * 간편견적 결과(인바운드) 목록 가져오기(DxDataGrid용)
 */
export function getHCostInboundDataSource(options: { isExport: boolean; searchType?: string; accessType?: string }) {
  let response;
  return new DataSource({
    store: new CustomStore({
      key: "id",
      onLoading() {
        // store.commit("store/setLoading", true);
      },
      onLoaded() {},
      load: async loadOptions => {
        // 파라미터 생성로
        if (options.isExport) {
          loadOptions = {
            ...loadOptions,
            take: 0,
            skip: 0,
            requireTotalCount: true,
          };
        }
        const params =
          CommonUtil.makeDxDataGridParams(loadOptions) +
          `${options.searchType ? "&searchType=" + options.searchType : null}`;
        try {
          if (options.accessType === CommonConst.ACCESS_TYPE_ADMIN) {
            response = await getAdminHCostResultInbounds(params);
          } else {
            response = await getHCostResultInbounds(params);
          }

          return response.data;
        } catch (e) {
          console.error(e);
          throw new Error("Data Loading Error");
        }
      },
      update: async (key, values) => {
        try {
          if (options.accessType === CommonConst.ACCESS_TYPE_ADMIN) {
            await editAdminHCostResultInbound(key, values, {
              headers: {
                "Content-Type": "application/json",
              },
            });
          } else {
            await editHCostResultInbound(key, values, {
              headers: {
                "Content-Type": "application/json",
              },
            });
          }
        } catch (e) {
          throw new Error("Data Update Error");
        }
      },
      remove: async (key): Promise<any> => {
        try {
          if (options.accessType === CommonConst.ACCESS_TYPE_ADMIN) {
            return await removeAdminHCostResultInbound(key);
          } else {
            return await removeHCostResultInbound(key);
          }
        } catch (e) {
          throw new Error("Data Delete Error");
        }
      },
    }),
    filter: options.accessType !== CommonConst.ACCESS_TYPE_ADMIN ? ["counselTaken", "=", true] : null,
  });
}

/**
 * 간편견적 결과 목록 가져오기(DxDataGrid용)
 */
export function getHCostResultDataSource(options: { isExport: boolean }) {
  let response;
  return new CustomStore({
    key: "id",
    onLoading() {
      // store.commit("store/setLoading", true);
    },
    onLoaded() {},
    load: async loadOptions => {
      // 파라미터 생성로
      if (options.isExport) {
        loadOptions = {
          ...loadOptions,
          take: 0,
          skip: 0,
          requireTotalCount: true,
        };
      }
      const params = CommonUtil.makeDxDataGridParams(loadOptions);
      try {
        response = await getHCostResults(params);

        return response.data;
      } catch (e) {
        throw new Error("Data Loading Error");
      }
    },
    update: async (key, values) => {
      try {
        await updateHCostResult(key, values, {
          headers: {
            "Content-Type": "application/json",
          },
        });
      } catch (e) {
        throw new Error("Data Update Error");
      }
    },
  });
}

/**
 * 간편견적 결과 조회하기
 */
export async function getHCostResult(resultId: number, isFactor: boolean, setHcostObject: any) {
  try {
    // 조회
    const { data } = await _getHCostResult(resultId);

    // 간편견적 요소 불러오기
    if (isFactor) {
      await getHCostFactorList(data, setHcostObject);
    }

    return data;
  } catch (e) {
    console.log(e);

    commonNotify(CommonConst.errorGet, "error");
    throw e;
  }
}

/**
 * 간편견적 인바운드 조회하기
 */
export async function getHCostResultInbound(id: number, accessType?: string) {
  try {
    // 조회
    if (accessType === CommonConst.ACCESS_TYPE_ADMIN) {
      const { data } = await _getAdminHCostResultInbound(id);

      return data;
    } else {
      const { data } = await _getHCostResultInbound(id);

      return data;
    }
  } catch (e) {
    console.log(e);
    commonNotify(CommonConst.errorGet, "error");
    throw e;
  }
}

/**
 * 간편견적 결과 삭제
 * @param resultId 간편견젹 결과 id
 */
export async function deleteHCostResult(resultId: number) {
  try {
    await removeHCostResultInbound(resultId);
    commonNotify(CommonConst.completeDelete);
  } catch (e: any) {
    console.log(e.response.data);
    commonNotify(CommonConst.errorDelete, "error");
    throw e;
  } finally {
  }
}

/**
 * 간편견적(운영자) 결과 삭제
 * @param resultId 간편견젹 결과 id
 */
export async function deleteAdminHCostResult(resultId: number) {
  try {
    await removeAdminHCostResultInbound(resultId);
    commonNotify(CommonConst.completeDelete);
  } catch (e: any) {
    console.log(e.response.data);
    commonNotify(CommonConst.errorDelete, "error");
    throw e;
  } finally {
  }
}

/**
 * 온실 모델을 결과 모델로 만들기
 */
function createModelToResult(hcostModel: HcostModel, hcostResult: HcostResult | HcostResultV2) {
  if (hcostResult.modelKey === hcostModel.modelKey) {
    let resultFactor: HcostResultFactor = null;
    let resultFactors: HcostResultFactor[] = [];
    for (const modelFactor of hcostModel.modelFactors) {
      if (modelFactor && modelFactor.factor && modelFactor.factorScope) {
        resultFactor = cloneDeep(HCOST_RESULT_FACTOR_DATA);
        resultFactor.factor = modelFactor.factor;
        resultFactor.factor.factorScopes = modelFactor.factor.factorScopes;
        resultFactor.factorScope = modelFactor.factorScope;
      }
      resultFactors.push(resultFactor);
    }

    return {
      ...hcostResult,
      basicLength: hcostModel.basicLength,
      basicBay: hcostModel.basicBay,
      modelTitle: hcostModel.modelTitle,
      basicStruct: hcostModel.basicStruct,
      basicAreaPyeong: hcostModel.basicAreaPyeong,
      basicWidth: hcostModel.basicWidth,
      basicHeightSide: hcostModel.basicHeightSide,
      basicLevel: hcostModel.basicLevel,
      totalCostAvg: hcostModel.totalCostAvg,
      totalCostMax: hcostModel.totalCostMax,
      totalCostMin: hcostModel.totalCostMin,
      costPerAreaPyeong: hcostModel.totalCostAvg / hcostModel.basicAreaPyeong,
      resultFactors: resultFactors,
    };
  }
}

/**
 * 선택된 모델요소 string 만들기
 */
function createSelectedResultFactors(hcostResult: HcostResult | HcostResultV2, hcostFactors: HcostFactor[]) {
  let data: any = {};
  // 초급자인 경우
  if (hcostResult.hcostMode === HCostConst.hcostMode.BIGINNER) {
    data.basicLength = 0;
    data.basicBay = 0;
  }

  data.resultFactors = [];
  let resultFactor: HcostResultFactor = null;
  for (const factor of hcostFactors) {
    if (factor.selectedNode && factor.selectedScopeKey && factor.factorScopes) {
      resultFactor = cloneDeep(HCOST_RESULT_FACTOR_DATA);
      resultFactor.factor = factor;
      resultFactor.factorScope = factor.factorScopes.find(
        (element: any) => element.scopeKey === factor.selectedScopeKey,
      );
      resultFactor.factorCat1 = factor.factorCat1;
      resultFactor.factorCat2 = factor.factorCat2;
      resultFactor.factorName = factor.factorName;
      resultFactor.costAvg = factor.selectedCostAvg;
      resultFactor.costMin = factor.selectedCostMax;
      resultFactor.costMax = factor.selectedCostMin;
      // eslint-disable-next-line no-loop-func
      factor.factorScopes.forEach((factor: any) => {
        if (factor.selectedScopeKey === factor.scopeKey) {
          resultFactor.factorValue = factor.scopeName;
          resultFactor.factorSelected = true;
        }
      });
      data.resultFactors.push(resultFactor);
    }
  }

  return {
    ...hcostResult,
    ...data,
  };
}

/**
 * 시공상담 파일 업로드용S3 Presigned Url 생성 요청
 */
export async function _getCounselFileUrl(accessType: string, id: number, fileType: string, fileName: string[]) {
  // store.commit("store/setLoading", true);
  let params = null;
  let result = null;
  let s3Model = null;

  try {
    params = `filetype=${fileType}&filenames=${fileName}`;
    if (accessType === CommonConst.ACCESS_TYPE_ADMIN) {
      result = await getCounselFileUrl(id, params);
    } else {
      result = await getCounselFileUrlInbound(id, params);
    }

    s3Model = result.data;
  } catch (e) {
    commonNotify(CommonConst.errorCommon, "error");
  } finally {
    // store.commit("store/setLoading", false);
  }
  return s3Model;
}

/**
 * 시공상담 파일 업로드용S3 Presigned Url 생성 요청
 */
export async function _getCounselPresignedurlFileUrl(accessType: string, id: number, attachedFileId: number) {
  // store.commit("store/setLoading", true);
  let result = null;
  let s3Model = null;

  try {
    if (accessType === CommonConst.ACCESS_TYPE_ADMIN) {
      result = await getCounselPresignedurlFileUrl(id, attachedFileId);
    } else {
      result = await getCounselPresignedurlFileUrlInbound(id, attachedFileId);
    }

    s3Model = result.data;
  } catch (e) {
    commonNotify(CommonConst.errorCommon, "error");
  } finally {
    // store.commit("store/setLoading", false);
  }
  return s3Model;
}

// 파일 다운로드
export async function downloadCounselFile(url: string, type: string, fileName: string) {
  // store.commit("store/setLoading", true);
  let res: AxiosResponse<any, any> = null;
  try {
    res = await Axios({
      method: "get",
      url,
      responseType: "blob",
    });
    const file = window.URL.createObjectURL(new Blob([res.data]));

    const docUrl = document.createElement("a");
    docUrl.href = file;
    docUrl.setAttribute("download", `${fileName}`);
    document.body.appendChild(docUrl);
    docUrl.click();
    docUrl.remove();
    window.URL.revokeObjectURL(file);
  } catch (e) {
    commonNotify(CommonConst.errorCommon, "error");
    throw e;
  } finally {
    // store.commit("store/setLoading", false);
  }
}

/**
 * 알고리즘: 면적 <- 동수, 폭, 길이
 */
function algorithm_basicAreaPyeong(hcostResult: HcostResult): any {
  if (hcostResult.hcostMode !== HCostConst.hcostMode.BIGINNER) {
    hcostResult.basicAreaPyeong = Math.ceil(
      (hcostResult.basicBay * hcostResult.basicWidth * hcostResult.basicLength) / 3.3058,
    );
    return hcostResult;
  }
}
